import { Injectable } from '@angular/core';
import {
  Alert,
  Convoy,
  Group,
  Operation,
  Operations,
  Period,
  Periods,
  SpeedingByDriver,
  TopEventsByDriver,
  TopGeneralEvents,
  TrapDistance,
  UnitOperation,
  UnitOperations,
} from '../models/dashboard';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { UnitService } from 'src/app/units/services/unit.service';
import { catchError, last, map, take, tap } from 'rxjs/operators';
import { Unit } from 'src/app/units/models/unit';
import { VehicleService } from 'src/app/vehicles/services/vehicle.service';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  periods: Periods = [];
  period$: BehaviorSubject<Period>;

  listUnitIMEIs$: BehaviorSubject<string[]>;
  eventsList$: BehaviorSubject<Alert[]>;
  operations$: BehaviorSubject<UnitOperations>;
  operation$: BehaviorSubject<UnitOperations>;
  idOfTxStates$: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([
    0, 1, 2, 3, 4, 5, 6,
  ]);

  // ----- Flags -----
  private imeiListLoaded$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  private eventsListLoaded$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  private isEverythingReady$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  private isNewDataRequested$: BehaviorSubject<number> =
    new BehaviorSubject<number>(0);
  // ----- X -----
  private requestTopDriverEvents$: BehaviorSubject<TopEventsByDriver[]> =
    new BehaviorSubject<TopEventsByDriver[]>([]);
  private requestTopGeneralEvents$: BehaviorSubject<TopGeneralEvents[]> =
    new BehaviorSubject<TopGeneralEvents[]>([]);
  private requestSpeedingByDriver$: BehaviorSubject<SpeedingByDriver[]> =
    new BehaviorSubject<SpeedingByDriver[]>([]);
  private requestVehicleTripDistance$: BehaviorSubject<TrapDistance[]> =
    new BehaviorSubject<TrapDistance[]>([]);

  constructor(private http: HttpClient, private unitService: UnitService, private vehicleService : VehicleService) {
    this.listUnitIMEIs$ = new BehaviorSubject<string[]>([]);
    this.eventsList$ = new BehaviorSubject<Alert[]>([]);
    this.operations$ = new BehaviorSubject<UnitOperations>([]);
    this.operation$ = new BehaviorSubject<UnitOperations>([]);
    this.initPeriods();
    this.vehicleService.initialize();
    this.unitService.getUnits
      .pipe(
        tap((_) => {
          if (_.length > 0) {
            this.operations$.next(
              this.getUnitOperationsFromUnits(_).sort((a, b) => a.code - b.code)
            );
            this.listUnitIMEIs$.next(_.map((T) => T.GNSSIMEI));
            if (this.imeiListLoaded$.getValue() == false) {
              this.imeiListLoaded$.next(true);
              this.checkIfEverythingIsReady();
            }
          }
        })
      )
      .subscribe();
    this.requestAllEventOfUser()
      .pipe(
        tap((_) => {
          if (typeof _ == 'object') {
            let data = _.data;
            if (Array.isArray(data)) {
              this.eventsList$.next(
                data.map(
                  (T): Alert => ({
                    id: T.id,
                    name: T.name,
                    type: T.type,
                    slug: T.slug,
                  })
                )
              );
            }
            this.eventsListLoaded$.next(true);
            this.checkIfEverythingIsReady();
          }
        })
      )
      .subscribe();
    this.period$ = new BehaviorSubject<Period>(this.periods[1]);
  }
  get getIdOfTxStates() {
    return this.idOfTxStates$.asObservable();
  }
  get getAllRedy() {
    return this.isEverythingReady$.asObservable();
  }
  get getIsNewDataRequested() {
    return this.isNewDataRequested$.asObservable();
  }
  get getPeriod() {
    return this.period$.asObservable();
  }
  get getRequestTopDriverEvents() {
    return this.requestTopDriverEvents$.asObservable();
  }
  get getRequestTopGeneralEvents() {
    return this.requestTopGeneralEvents$.asObservable();
  }
  get getRequestSpeedingByDriver() {
    return this.requestSpeedingByDriver$.asObservable();
  }
  get getRequestVehicleTripDistance() {
    return this.requestVehicleTripDistance$.asObservable();
  }
  get getUnitOperationSelected() {
    return this.operation$.asObservable();
  }

  getCurrentSpeedingByDriver() {
    return this.requestSpeedingByDriver$.getValue();
  }
  getCurrentTopDriverEvents() {
    return this.requestTopDriverEvents$.getValue();
  }
  getCurrentTopGeneralEvents() {
    return this.requestTopGeneralEvents$.getValue();
  }
  getCurrentVehicleTripDistance() {
    return this.requestVehicleTripDistance$.getValue();
  }

  initPeriods(): void {
    const today = new Date();
    const yesterday = new Date(today);
    const theDayBeforeYesterday = new Date(today);
    const threeDaysAgo = new Date(today);
    const fourDaysAgo = new Date(today);
    const fiveDaysAgo = new Date(today);
    const sixDaysAgo = new Date(today);
    const weekly = new Date(today);
    const monthly = new Date(today);
    const fortnightly = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    theDayBeforeYesterday.setDate(today.getDate() - 2);
    threeDaysAgo.setDate(today.getDate() - 3);
    fourDaysAgo.setDate(today.getDate() - 4);
    fiveDaysAgo.setDate(today.getDate() - 5);
    sixDaysAgo.setDate(today.getDate() - 6);
    weekly.setDate(yesterday.getDate() - 6);
    fortnightly.setDate(yesterday.getDate() - 14);
    monthly.setDate(yesterday.getDate() - 29);
    const pds: Periods = [
      {
        id: 1,
        name: 'Hoy',
        start: new Date(today.setHours(0, 0, 0, 0)),
        end: new Date(today.setHours(23, 59, 59, 999)),
      },
      {
        id: 2,
        name: 'Ayer',
        start: new Date(yesterday.setHours(0, 0, 0, 0)),
        end: new Date(yesterday.setHours(23, 59, 59, 999)),
        checked: true,
      },
      {
        id: 3,
        name: 'hace 2 dias', //theDayBeforeYesterday.toLocaleDateString(),
        start: new Date(theDayBeforeYesterday.setHours(0, 0, 0, 0)),
        end: new Date(theDayBeforeYesterday.setHours(23, 59, 59, 999)),
        disabled: false,
        show: true,
      },
      {
        id: 4,
        name: 'hace 3 dias', //threeDaysAgo.toLocaleDateString(),
        start: new Date(threeDaysAgo.setHours(0, 0, 0, 0)),
        end: new Date(threeDaysAgo.setHours(23, 59, 59, 999)),
        disabled: true,
        show: false,
      },
      {
        id: 5,
        name: 'hace 4 dias', //fourDaysAgo.toLocaleDateString(),
        start: new Date(fourDaysAgo.setHours(0, 0, 0, 0)),
        end: new Date(fourDaysAgo.setHours(23, 59, 59, 999)),
        disabled: true,
        show: false,
      },
      {
        id: 6,
        name: 'hace 5 dias', //fiveDaysAgo.toLocaleDateString(),
        start: new Date(fiveDaysAgo.setHours(0, 0, 0, 0)),
        end: new Date(fiveDaysAgo.setHours(23, 59, 59, 999)),
        disabled: true,
        show: false,
      },
      {
        id: 7,
        name: 'hace 6 dias', //sixDaysAgo.toLocaleDateString(),
        start: new Date(sixDaysAgo.setHours(0, 0, 0, 0)),
        end: new Date(sixDaysAgo.setHours(23, 59, 59, 999)),
        disabled: true,
        show: false,
      },
      {
        id: 8,
        name: 'hace una semana',
        start: new Date(weekly.setHours(0, 0, 0, 0)),
        end: new Date(yesterday.setHours(23, 59, 59, 999)),
        disabled: false,
        show: true,
      },
      {
        id: 9,
        name: 'hace una quincena',
        start: new Date(fortnightly.setHours(0, 0, 0, 0)),
        end: new Date(yesterday.setHours(23, 59, 59, 999)),
        disabled: false,
        show: true,
      },
      {
        id: 10,
        name: 'hace un mes',
        start: new Date(monthly.setHours(0, 0, 0, 0)),
        end: new Date(yesterday.setHours(23, 59, 59, 999)),
        disabled: false,
        show: true,
      },
    ];
    this.periods = pds.filter((p) => p.show != false);
  }

  checkIfEverythingIsReady(): void {
    const everythingIsReady =
      this.imeiListLoaded$.getValue() && this.eventsListLoaded$.getValue();
    if (everythingIsReady) {
      this.getAllRequest();
    }
    this.isEverythingReady$.next(everythingIsReady);
  }
  setPeriod(period: Period): void {
    this.period$.next(period);
  }
  getUtcDateTimeString(date: Date): string {
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
      .toISOString()
      .replace('T', ' ')
      .split('.')[0];
  }
  getAllRequest() {
    this.isNewDataRequested$.next(this.isNewDataRequested$.getValue() + 1);
    this.requestTopDriverEvents();
    this.requestTopGeneralEvents();
    this.requestSpeedingByDriver();
    this.requestVehicleTripDistance();
  }
  requestAllEventOfUser(): Observable<any> {
    return this.http.get<any>(environment.apiUrl + '/api/events');
  }
  resendObservableData() {
    this.requestTopDriverEvents$.next(this.requestTopDriverEvents$.getValue());
    this.requestTopGeneralEvents$.next(
      this.requestTopGeneralEvents$.getValue()
    );
    this.requestSpeedingByDriver$.next(
      this.requestSpeedingByDriver$.getValue()
    );
    this.requestVehicleTripDistance$.next(
      this.requestVehicleTripDistance$.getValue()
    );
  }
  resendTripDistanceObservableData() {
    this.requestVehicleTripDistance$.next(
      this.requestVehicleTripDistance$.getValue()
    );
  }
  private requestTopDriverEvents(): void {
    const query = this.http
      .post<any>(environment.apiUrl + '/api/dashboardTopEventsDrivers', {
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        date_start: this.getUtcDateTimeString(this.period$.getValue().start),
        date_end: this.getUtcDateTimeString(this.period$.getValue().end),
      })
      .pipe(
        map((response: any) => {
          if (response && typeof response === 'object' && response.data) {
            return response.data.map(
              (item: any): TopEventsByDriver => ({
                imei: item.tracker_imei,
                driverId: parseInt(item.driver_id),
                driverName: item.driver_name,
                event: item.descripcion_evento,
                dateTracker: item.fecha_tracker_day,
                events: item.total_events,
                confirmed: item.evaluations_confirmed,
                notConfirmed: item.evaluations_not_confirmed,
              })
            );
          } else {
            return [];
          }
        }),
        tap((_) => {
          this.requestTopDriverEvents$.next(_);
        })
      )
      .subscribe();
    // query.unsubscribe();
  }
  private requestTopGeneralEvents(): void {
    const query = this.http
      .post<any>(environment.apiUrl + '/api/dashboardTopGeneralEvents', {
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        date_start: this.getUtcDateTimeString(this.period$.getValue().start),
        date_end: this.getUtcDateTimeString(this.period$.getValue().end),
      })
      .pipe(
        map((response: any) => {
          if (response && typeof response === 'object' && response.data) {
            return response.data.map(
              (item: any): TopGeneralEvents => ({
                imei: item.tracker_imei,
                event: item.descripcion_evento,
                events: item.total_events,
                confirmed: item.total_evaluations_confirmed,
              })
            );
          } else {
            return [];
          }
        }),
        tap((_) => {
          this.requestTopGeneralEvents$.next(_);
        })
      )
      .subscribe();
    // query.unsubscribe();
  }
  private requestSpeedingByDriver(): void {
    const query = this.http
      .post<any>(environment.apiUrl + '/api/dashboardSpeedingDrivers', {
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        date_start: this.getUtcDateTimeString(this.period$.getValue().start),
        date_end: this.getUtcDateTimeString(this.period$.getValue().end),
      })
      .pipe(
        map((response: any) => {
          if (response && typeof response === 'object' && response.data) {
            return response.data.map(
              (item: any): SpeedingByDriver => ({
                imei: item.tracker_imei,
                driverId: parseInt(item.driver_id),
                driverName: item.driver_name,
                zoneId: item.zone_id,
                zoneName: item.zone_name,
                level1: item.count_nivel_i,
                level2: item.count_nivel_ii,
                level3: item.count_nivel_iii,
              })
            );
          } else {
            throw new Error('Invalid response format');
          }
        }),
        tap((_) => {
          this.requestSpeedingByDriver$.next(_);
        })
      )
      .subscribe();
    // query.unsubscribe();
  }
  private requestVehicleTripDistance(): void {
    const query = this.http
      .post<any>(environment.apiUrl + '/api/dashboardTrackersDistance', {
        imeis: this.listUnitIMEIs$.getValue(), // this.unitService.
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        date_start: this.getUtcDateTimeString(this.period$.getValue().start),
        date_end: this.getUtcDateTimeString(this.period$.getValue().end),
      })
      .pipe(
        map((response: any) => {
          if (response && typeof response === 'object' && response.data) {
            return response.data.map((item: any): TrapDistance => {
              const totalDistance = item.totalDistance ?? 0;
              const totalDistanceCAN = Math.abs(item.totalDistanceCAN ?? 0);
              const threshold = 1000;
              if (
                totalDistanceCAN > totalDistance &&
                totalDistanceCAN - totalDistance > threshold
              ) {
                return {
                  imei: item.imei ?? '',
                  distance: totalDistance,
                  distanceCAN: totalDistance,
                  count: item.count ?? 0,
                  fuel: item.totalFuelCAN ?? 0,
                };
              }
              return {
                imei: item.imei ?? '',
                distance: totalDistance,
                distanceCAN: totalDistanceCAN,
                count: item.count ?? 0,
                fuel: item.totalFuelCAN ?? 0,
              };
            });
          } else {
            // throw new Error('Invalid response format');
            // console.log(response);
            return [];
          }
        }),
        catchError((error: any) => {
          // console.error('Error fetching vehicle trip distance', error);
          return of([]); // Retorna un array vacío en caso de error
        }),
        tap((_) => {
          this.requestVehicleTripDistance$.next(_);
        })
      )
      .subscribe(
        () => {}, // No es necesario hacer nada aquí si ya manejas la respuesta en el pipe
        (error) => {
          console.error('Error en la suscripción:', error); // Esto captura errores no manejados en el pipe
        }
      );
  }

  getUnitOperationsFromUnits(units: Unit[]): UnitOperations {
    let unitOperations: UnitOperations = [];
    units.map((unit) => {
      if (!unitOperations.some((_) => _.code == unit.idOperation)) {
        unitOperations.push({
          name: unit.operation,
          code: unit.idOperation,
        });
      }
    });
    return unitOperations;
  }
  getOperationsFromUnits(units: Unit[]): Operations {
    let operations: Operations = [];
    units.map((unit) => {
      let o = this.getOperationFromUnit(unit);
      if (!operations.some((_) => _.id === o.id)) {
        operations.push(o);
      }
    });
    units.map((unit) => {
      let group = this.getGroupFromUnit(unit);
      operations.map((o) => {
        if (o.id === unit.idOperation) {
          if (!o.groups.some((_) => _.id === group.id)) {
            o.groups.push(group);
          }
        }
      });
    });
    units.map((unit) => {
      let convoy = this.getConvoyFromUnit(unit);
      operations.map((o) => {
        o.groups.map((g) => {
          if (g.id === unit.idGrupo) {
            if (!g.convoys.some((_) => _.id === convoy.id)) {
              g.convoys.push(convoy);
            }
          }
        });
      });
    });
    return operations;
  }
  getOperationFromUnit(unit: Unit): Operation {
    let operation: Operation = {
      id: unit.idOperation,
      name: unit.operation,
      groups: [],
    };
    return operation;
  }
  getGroupFromUnit(unit: Unit): Group {
    let group: Group = {
      id: unit.idGrupo,
      name: unit.group,
      convoys: [],
    };
    return group;
  }
  getConvoyFromUnit(unit: Unit): Convoy {
    let convoy: Convoy = {
      id: unit.idConvoy,
      name: unit.convoy,
    };
    return convoy;
  }

  randomNumber(first: number, last: number) {
    return Math.floor(Math.random() * (last - first + 1)) + first;
  }

  async obtenerUbicacion(): Promise<GeolocationPosition | null> {
    try {
      const permiso = await navigator.permissions.query({
        name: 'geolocation' as PermissionName,
      });

      if (permiso.state === 'granted' || permiso.state === 'prompt') {
        return new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            (pos) => resolve(pos),
            (err) => {
              console.error('Error al obtener ubicación:', err.message);
              resolve(null); // no rechazamos, devolvemos null
            },
            {
              enableHighAccuracy: true,
              timeout: 5000,
              maximumAge: 0,
            }
          );
        });
      } else {
        const permiso = await navigator.permissions.query({
          name: 'geolocation' as PermissionName,
        });

        if (permiso.state === 'denied') {
          alert(
            'La ubicación está bloqueada. Ve a la configuración del navegador para activarla.'
          );
        }

        console.warn('Permiso de geolocalización denegado');
        return null;
      }
    } catch (error) {
      console.error('Error consultando permisos:', error);
      return null;
    }
  }
}
